import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { ROLES } from './constants';
import { getLevel, getOrgPath, getDescendantIds, getLevel2Ancestor } from '../utils/org';

const AuthContext = createContext(null);

const STORAGE_KEY = 'crc_sms_user';

function getStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// 为子公司用户预计算组织层级信息（子孙集小，登录后算一次）
function buildOrgContext(userData) {
  if (!userData || userData.role !== ROLES.SUBSIDIARY) return {};
  const orgNode = userData.orgNode || userData.subsidiary;
  if (!orgNode) return {};
  return {
    orgNode,
    orgLevel: getLevel(orgNode),
    orgPath: getOrgPath(orgNode),
    orgDescendantIds: [...getDescendantIds(orgNode)],
    orgLevel2Id: getLevel2Ancestor(orgNode),
    // 别名保持向后兼容
    subsidiary: orgNode,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());

  const login = useCallback((userData, authMeta = {}) => {
    const orgCtx = buildOrgContext(userData);
    const sessionUser = {
      ...userData,
      ...orgCtx,
      authMeta: {
        loginIdentifier: authMeta.loginIdentifier,
        loginIdentifierType: authMeta.loginIdentifierType,
        verificationChannel: authMeta.verificationChannel,
        loginAt: authMeta.loginAt || new Date().toISOString(),
      },
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionUser));
    setUser(sessionUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setUser(null);
  }, []);

  const isGroup = user?.role === ROLES.GROUP;
  const isSubsidiary = user?.role === ROLES.SUBSIDIARY;
  const isSupplier = user?.role === ROLES.SUPPLIER;
  const orgLevel = user?.orgLevel;
  const isLevel2 = isSubsidiary && orgLevel === 2;
  const isLevel3or4 = isSubsidiary && (orgLevel === 3 || orgLevel === 4);

  const value = useMemo(
    () => ({
      user,
      login,
      logout,
      isGroup,
      isSubsidiary,
      isSupplier,
      isLevel2,
      isLevel3or4,
      orgLevel,
      orgDescendantIds: user?.orgDescendantIds || null,
      orgLevel2Id: user?.orgLevel2Id || null,
    }),
    [user, login, logout, isGroup, isSubsidiary, isSupplier, isLevel2, isLevel3or4, orgLevel]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
